import { safeParseJSON } from '@immediate_media/script-utils';
import {
  findPlaceholdersForLongForm,
  findPlaceholdersForRecipes,
  findEmptyDivPlaceholders,
  getIngredientsCount,
} from './find-placeholders.js';
import { filterSlotsSizes, getHeightToReserve } from './filter-sizes.js';
import { convertToSlot } from '../define.js';
import { adSlotsLazyLoad } from './ad-slots-lazy-load.js';

import '../typedef.js';

const PLACEHOLDER_SELECTOR = 'div.ad-placement';
const LIST_SELECTOR_CLASS = 'section.recipe__ingredients li';
const SLOT_INLINE_TARGETING_KEY = 'inline-ad';

/**
 * @param {string} unitName
 * @returns ad unit name if string is not empty or not null
 */
const getUnitName = (unitName) => ((unitName.trim() === '') ? null : unitName);

/**
 * @param {SlotConfig} slotConfig
 * @param {number} index
 * @returns transformed slot configuration later to be passed to GPT
 */
const transformAdConfigs = (slotConfig, index) => ({
  id:
    slotConfig.indexPosition !== undefined && slotConfig.indexPosition >= 0
      ? `${slotConfig.id}-${slotConfig.indexPosition}`
      : `${slotConfig.id}-${index + 1}`,
  class: slotConfig.class,
  targets: slotConfig.adTargets || {},
  sizes: safeParseJSON(slotConfig.adSizes) || [],
  unitName: (slotConfig.adUnitName) ? getUnitName(slotConfig.adUnitName) : null,
});

/**
 * @param {string} type
 * @returns text for ad disclaimer
 */
const getAdDisclaimerText = (type) => `${type === 'recipes' ? 'Recipe' : 'Article'} continues below advert`;

const adDisclaimerStyle = `
  text-align: center; 
  font-family: Helvetica,Arial,sans-serif!important;
  font-size: 10px!important;
  box-shadow: none!important;
  color: #3d3d3d;
  height: 30px;
  margin: auto;
  line-height: 30px;  
`;

const adSlotContainerStyle = `
  position: sticky;
  top: 44px;
  width: 300px;
  height: fit-content;
  background: #CCCCCC;
  margin: auto;
`;

const placeholderStyle = `
  width: 100vw; 
  margin-left: -10px; 
  background: rgb(245, 245, 245); 
  padding-bottom: 30px;
`;

/**
 * Updates placeholder HTML code
 *
 * @param {Element} placeholder
 * @param {SlotConfig} slotConfig
 * @param {number} reservedHeight
 * @param {string} type
 */
const updateAdPlaceholder = (placeholder, slotConfig, reservedHeight, type) => {
  if (!placeholder || !slotConfig || !reservedHeight || !type) { return; }

  const adDisclaimer = document.createElement('div');
  const adSlotContainer = document.createElement('div');
  const adSlotElement = document.createElement('div');

  adDisclaimer.setAttribute('style', adDisclaimerStyle);
  adDisclaimer.innerText = getAdDisclaimerText(type);

  adSlotContainer.setAttribute('style', adSlotContainerStyle);

  adSlotElement.setAttribute('id', slotConfig.id);

  if (reservedHeight > 0) {
    adSlotElement.setAttribute('style', `height: ${reservedHeight}px`);
  }

  if (slotConfig.class !== '') {
    slotConfig.class.split(' ').forEach((element) => {
      adSlotElement.classList.add(element);
    });
  }

  placeholder.setAttribute('style', placeholderStyle);
  placeholder.appendChild(adDisclaimer);
  placeholder.appendChild(adSlotContainer);

  adSlotContainer.appendChild(adSlotElement);
};

/**
 * @param {Array.<Element>} emptyPlaceholders
 * @param {Object} globalSettings
 * @param {AdConfig} adConfig
 */

const matchPlaceholders = (emptyPlaceholders, globalSettings, adConfig) => {
  // In later phase when every will have its own config change this to use
  // page category from ad-config.json file
  if (globalSettings.targets?.channel === 'recipes') {
    const ingredientsCount = getIngredientsCount(LIST_SELECTOR_CLASS);
    return findPlaceholdersForRecipes(emptyPlaceholders, ingredientsCount, {
      charLimit: adConfig.characterLimit.recipes,
    });
  }

  return findPlaceholdersForLongForm(emptyPlaceholders, {
    charLimit: adConfig.characterLimit.longForm,
    maxAds: adConfig.maxAds,
  });
};

/**
 *
 * @param {Array.<SlotConfig>} slotConfigs
 * Checks if any placeholder is matched and adds to gpt additional targeting
 */
export const addGlobalInlineTargeting = (slotConfigs) => {
  googletag.cmd.push(() => {
    googletag.pubads().setTargeting('kw', slotConfigs.length > 0 ? 'inline' : 'no_inline');
  });
};

/**
 * @param {Object} globalSettings
 * @param {AdConfig} adConfig
 * @returns found empty div placeholders, depending on webisite type
 */
export const getDynamicAdsPlaceholders = (globalSettings, adConfig) => {
  const emptyPlaceholders = findEmptyDivPlaceholders(PLACEHOLDER_SELECTOR);

  return matchPlaceholders(emptyPlaceholders, globalSettings, adConfig);
};

/**
 * @param {Array.<Element>} placeholders
 * @param {Array.<AdSlot>} dynamicAdSlots
 * @returns prepared matched empty div placeholers for ad slots
 */
export const prepareAdSlotsConfig = (placeholders, dynamicAdSlots) => {
  const indexedAdSlotConfigs = dynamicAdSlots.filter(
    (dynamicAdSlot) => dynamicAdSlot.indexPosition !== undefined
      && dynamicAdSlot.indexPosition >= 0
      && placeholders?.[dynamicAdSlot.indexPosition - 1],
  );
  const defaultSlotConfig = dynamicAdSlots
    .filter((dynamicAdSlot) => dynamicAdSlot.default)
    .shift();

  const slotsConfig = new Array(placeholders.length).fill(defaultSlotConfig);

  if (indexedAdSlotConfigs.length > 0) {
    indexedAdSlotConfigs.forEach((indexedAdSlot) => {
      if (slotsConfig[indexedAdSlot.indexPosition - 1]) {
        slotsConfig[indexedAdSlot.indexPosition - 1] = indexedAdSlot;
      }
    });
  }

  return slotsConfig;
};

/**
 * @param {Array.<SlotConfig>} slotsConfig
 * @returns enriched with gpt definition of the ad slot and filtered sizes and
 *    slots that failed to be defined in gpt
 */
export const enrichWithFilteredGptAdSlots = (slotsConfig) => filterSlotsSizes(
  slotsConfig.map(transformAdConfigs),
  window.innerWidth,
).map((slot, index) => ({
  ...slot,
  targets: {
    pos: [
      ...slot.targets?.pos,
      `${SLOT_INLINE_TARGETING_KEY}-${index + 1}`,
    ],
  },
})).map((slot) => ({
  ...slot,
  gptSlot: convertToSlot(slot),
})).filter((slot) => slot.gptSlot);

/**
 * @param {Array.<Element>} placeholders
 * @param {Array.<SlotConfig>} slotsConfig
 * @param {string} type
 * @returns defined dynamic ad slots
 */
export const getEnrichedDynamicAdSlots = (placeholders, slotsConfig, type) => {
  addGlobalInlineTargeting(slotsConfig);
  const enrichedDynamicAdSlots = enrichWithFilteredGptAdSlots(slotsConfig);

  placeholders.forEach((placeholder, index) => {
    if (enrichedDynamicAdSlots[index]) {
      updateAdPlaceholder(
        placeholder,
        enrichedDynamicAdSlots[index],
        getHeightToReserve(enrichedDynamicAdSlots[index]?.sizes),
        type,
      );
    }
  });

  return enrichedDynamicAdSlots;
};

/**
 * @param {AdConfig} adConfig
 * @param {Object} globalSettings
 */
export const getDynamicAdsConfig = (adConfig, globalSettings) => ({
  ...adConfig,
  pageCategory: globalSettings.targets?.channel,
  maxAds: globalSettings.stitcher?.adInjection?.articles?.maxAds
    ? globalSettings.stitcher?.adInjection?.articles?.maxAds
    : adConfig.maxAds,
});

/**
 * Runs logic to display dynamic ads on mobile devices
 *
 * @param {Array.<Element>} placeholders
 * @param {AdConfig} adConfig
 * @param {Object} globalSettings
 */
export const loadDynamicAds = (placeholders, adConfig, globalSettings) => {
  const config = getDynamicAdsConfig(adConfig, globalSettings);
  const type = globalSettings.targets?.channel;

  const dynamicAdsSlotConfigs = prepareAdSlotsConfig(placeholders, config.adSlots.dynamic);
  const dynamicSlots = getEnrichedDynamicAdSlots(placeholders, dynamicAdsSlotConfigs, type);

  adSlotsLazyLoad(type, dynamicSlots);
};
